import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CommonTable2, {
  type Column,
} from "../../.MODULS/BOOKLET_MODUL/Components/CommonTable/CommonTable2";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { OverallActivityFilter } from "../../Redux/Slices/GateAdminDashboard/OverallActivityFilter";

interface ActivityItem extends Record<string, unknown> {
  id: number;
  title: string;
  description: string;
  time: string;
}

interface ActivityFeedProps {
  items: ActivityItem[];
}

const getColor = (title: string): string => {
  const lower = title.toLowerCase();

  if (lower.includes("exam")) return "#A66DE9"; // Purple
  if (lower.includes("test")) return "#E67E22"; // Orange
  if (lower.includes("practice")) return "#27AE60"; // Green
  if (lower.includes("user")) return "#3498DB"; // Blue
  if (lower.includes("payment")) return "#F39C12"; // Gold
  if (lower.includes("subscription")) return "#9B59B6"; // Violet
  if (lower.includes("registration")) return "#16A085"; // Teal
  if (lower.includes("manual")) return "#C0392B"; // Red
  if (lower.includes("attempt")) return "#8E44AD"; // Deep Purple
  if (lower.includes("completed")) return "#2ECC71"; // Light Green

  return "#95A5A6"; // Default grey
};


const ActivityFeed: React.FC<any> = ({ items }) => {
  const dispatch = useAppDispatch();

  const [dialogOpen, setDialogOpen] = useState(false);
  const { isLoading } = useAppSelector((state) => state.OverallActivityFilter);
  console.log(items, "items");

  const transformedActivityFeed = Array.isArray(items?.timeline)
    ? items.timeline.map((item: any, index: number) => {
        const type = item.type;
        const user = item.data?.user?.username ?? "Unknown User";
        const timestamp = new Date(item.timestamp);
        const formattedTime = `${
          timestamp.toISOString().split("T")[0]
        } | ${timestamp.toTimeString().slice(0, 5)}`;

        let title = "";
        let description = "";

        switch (type) {
          case "exam_attempt": {
            const examName = item.data?.exam?.name ?? "Unknown Exam";
            const score = item.data.attempt.scores.percentage
            const user = item.data?.user?.username ?? "Unknown User";
            title = "Exam Attempted";
            description = `${user} – ${examName}${
              score !== null && score !== undefined ? ` – Score: ${score}%` : ""
            }`;
            break;
          }

          case "practice_test_attempt": {
            const testName = item.data?.test?.name ?? "Unknown Test";
             const user = item.data?.user?.username ?? "Unknown User";
            const score = item.data.attempt.scores.percentage
            title = "Practice Test Completed";
            description = `${user} – ${testName}${
              score !== null && score !== undefined ? ` – Score: ${score}%` : ""
            }`;
            break;
          }

          case "user_registration": {
            title = "New User Registered";
             const user = item.data?.user?.username ?? "Unknown User";
            const method = item.data?.registration_type
              ? `${item.data.registration_type}`
              : "Manually Added (Free)";
             description = `${user} – ${method}`;
            break;
          }

          case "stripe_subscription": {
            title = "Payment Made";
             const user = item.data?.user?.username ?? "Unknown User";
            const amount = item.data?.plan?.cost ?? 0;
            const plan = item.data?.plan?.name ?? "Subscription Plan";
            description = `${user} – ${plan} – ₹${amount}`;
            break;
          }

          case "manual_subscription": {
            title = "Manual Subscription Activated";
             const user = item.data?.user?.username ?? "Unknown User";
               const amount = item.data?.plan?.cost ?? 0;
            const plan = item.data?.plan?.name ?? "Unknown Plan";
            // const duration = item.data?.plan?.duration ?? "";
           description = `${user} – ${plan} – ₹${amount}`;
            break;
          }

          default:
            title = "Activity";
            description = `${type}`;
        }

        return {
          id: index + 1,
          title,
          username: user, // 👈 added for the new column
          description,
          time: formattedTime,
        };
      })
    : [];

  const columns: Column[] = [
    { field: "id", headerName: "ID", sortable: true,fontWeight:400 },
    { field: "title", headerName: "Title", fontWeight:400 },
    { field: "username", headerName: "Username" ,fontWeight:400},
    { field: "description", headerName: "Description",fontWeight:400 },
    { field: "time", headerName: "Time" ,fontWeight:400},
  ];

  useEffect(() => {
  if (dialogOpen) {
    dispatch(OverallActivityFilter({ limit: 100 }));
  }
}, [dialogOpen, dispatch]);

  const handleOpenDialog = () => setDialogOpen(true);
const handleCloseDialog = () => {
  setDialogOpen(false);
  dispatch(OverallActivityFilter({ limit: 10 }));
};
  return (
    <>
      {/* ---------- ACTIVITY CARD ---------- */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 2.5,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
            flexShrink={0}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="primary"
              sx={{ fontSize: "15px" }}
            >
              Real Time Activity Feed
            </Typography>

            <Box
              sx={{
                backgroundColor: "#017CC9",
                px: 1.5,
                py: 0.4,
                borderRadius: 1,
                fontSize: "12px",
                cursor: "pointer",
                fontWeight: 500,
                color: "#ffffff",
              }}
              onClick={handleOpenDialog}
            >
              View All ▾
            </Box>
          </Box>

          {/* Scrollable List */}
          <Box sx={{ overflowY: "auto", pr: 1, flex: 1 }}>
            <List disablePadding>
              {transformedActivityFeed.map((item: ActivityItem) => (
                <ListItem
                  key={item.id}
                  alignItems="flex-start"
                  sx={{
                    position: "relative",
                    pl: 4,
                    pb: 1.5,
                    "&:not(:last-child)::after": {
                      content: '""',
                      position: "absolute",
                      left: 14,
                      top: 25,
                      bottom: -5,
                      width: "2px",
                      backgroundColor: "#E0E0E0",
                    },
                  }}
                >
                  {/* Colored Dot */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 10,
                      top: 10,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: getColor(item.title),
                    }}
                  />

                  <ListItemText
                    primary={
                      <Typography
                        fontWeight={600}
                        sx={{ fontSize: "14px", mb: 0.3 }}
                      >
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "14px", flex: 1, pr: 1 }}
                          noWrap
                        >
                          {item.description}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{
                            whiteSpace: "nowrap",
                            fontSize: "13px",
                          }}
                        >
                          {item.time}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </CardContent>
      </Card>

      {/* ---------- DIALOG WITH COMMON TABLE ---------- */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
          }}
        >
          Activity Feed Details
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <CommonTable2 columns={columns} rows={transformedActivityFeed} loading={isLoading} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActivityFeed;
